import {
  Button,
  Chip,
  MenuItem,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getLocale } from "../../../i18n";
import { useTranslation } from "../../../i18n/useTranslation";
import {
  EvidenceListQuery,
  EvidenceService,
} from "../../../services/evidence.service";
import Loading from "../../components/Loading";
import "./index.scss";

type EvidenceTag = {
  type: string;
  at?: string | null;
  logId?: string;
  faceId?: string;
  empName?: string;
  text?: string;
  scannedAt?: string;
};

type EvidenceItem = {
  _id: string;
  clipId: string;
  deviceId: string;
  date: string;
  startedAt: string;
  durationMs: number;
  sizeBytes: number;
  rotation?: number;
  isProtected: boolean;
  greetingWithoutLog: boolean;
  hasPunch: boolean;
  hasGreeting: boolean;
  hasFail: boolean;
  empNames: string[];
  faceIds: string[];
  logIds: string[];
  tags?: EvidenceTag[];
};

const PAGE_SIZE = 50;
const todayYmd = () => new Date().toISOString().slice(0, 10);

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour12: false,
  });
};

/** Admin: search + replay attendance motion evidence clips uploaded by check-in tablets. */
const AttendanceEvidence: React.FC = () => {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const [query, setQuery] = useState<EvidenceListQuery>({
    from: params.get("from") || todayYmd(),
    to: params.get("to") || todayYmd(),
    deviceId: params.get("deviceId") || "",
    employee: params.get("employee") || "",
    faceId: params.get("faceId") || "",
    logId: params.get("logId") || "",
    flag: params.get("flag") || "",
  });
  const [items, setItems] = useState<EvidenceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<EvidenceItem | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [videoError, setVideoError] = useState("");
  const urlRef = useRef<string>("");

  const load = useCallback(async (nextPage: number) => {
    setLoading(true);
    const res = await EvidenceService.list({ ...query, page: nextPage, limit: PAGE_SIZE });
    const data = res?.data?.data;
    setItems(data?.items || []);
    setTotal(data?.total || 0);
    setPage(nextPage);
    setLoading(false);
  }, [query]);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const play = async (item: EvidenceItem) => {
    setVideoError("");
    const detail = await EvidenceService.detail(item._id);
    setSelected(detail?.data?.data || item);
    const res = await EvidenceService.video(item._id);
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    if (res?.status === 200 && res.data) {
      urlRef.current = URL.createObjectURL(res.data);
      setVideoUrl(urlRef.current);
    } else {
      urlRef.current = "";
      setVideoUrl("");
      setVideoError(t("evidence.videoError"));
    }
  };

  const setField = (key: keyof EvidenceListQuery) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setQuery((q) => ({ ...q, [key]: e.target.value }));

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="attendance-evidence">
      <h2>{t("evidence.title")}</h2>
      <p className="attendance-evidence__hint">{t("evidence.privacyHint")}</p>
      <div className="attendance-evidence__filters">
        <TextField size="small" type="date" label={t("evidence.from")} value={query.from} onChange={setField("from")} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={t("evidence.to")} value={query.to} onChange={setField("to")} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label={t("evidence.device")} value={query.deviceId} onChange={setField("deviceId")} />
        <TextField size="small" label={t("evidence.employee")} value={query.employee} onChange={setField("employee")} />
        <TextField size="small" label="Face ID" value={query.faceId} onChange={setField("faceId")} />
        <TextField size="small" label="Log ID" value={query.logId} onChange={setField("logId")} />
        <TextField size="small" select label={t("evidence.flag")} value={query.flag} onChange={setField("flag")} style={{ minWidth: 200 }}>
          <MenuItem value="">{t("evidence.flagAll")}</MenuItem>
          <MenuItem value="greeting_without_log">{t("evidence.flagGreetingWithoutLog")}</MenuItem>
          <MenuItem value="fail">{t("evidence.flagFail")}</MenuItem>
          <MenuItem value="punch">{t("evidence.flagPunch")}</MenuItem>
          <MenuItem value="protected">{t("evidence.flagProtected")}</MenuItem>
        </TextField>
        <Button variant="contained" onClick={() => load(1)}>{t("evidence.search")}</Button>
      </div>

      <div className="attendance-evidence__body">
        <div className="attendance-evidence__list">
          {loading ? <Loading /> : (
            <table>
              <thead>
                <tr>
                  <th>{t("evidence.time")}</th>
                  <th>{t("evidence.device")}</th>
                  <th>{t("evidence.employee")}</th>
                  <th>{t("evidence.flags")}</th>
                  <th>{t("evidence.length")}</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5}>{t("evidence.empty")}</td></tr>
                )}
                {items.map((item) => (
                  <tr
                    key={item._id}
                    className={selected?._id === item._id ? "is-selected" : ""}
                    onClick={() => play(item)}
                  >
                    <td>{formatDateTime(item.startedAt)}</td>
                    <td>{item.deviceId}</td>
                    <td>{(item.empNames || []).join(", ") || "-"}</td>
                    <td>
                      {item.greetingWithoutLog && <Chip size="small" color="warning" label={t("evidence.flagGreetingWithoutLog")} />}
                      {item.hasFail && <Chip size="small" color="error" label={t("evidence.flagFail")} />}
                      {item.hasPunch && <Chip size="small" color="success" label={t("evidence.flagPunch")} />}
                    </td>
                    <td>{Math.round((item.durationMs || 0) / 1000)}s · {Math.round((item.sizeBytes || 0) / 1024)}KB</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="attendance-evidence__pager">
            <Button disabled={page <= 1} onClick={() => load(page - 1)}>‹</Button>
            <span>{page}/{totalPages} · {total}</span>
            <Button disabled={page >= totalPages} onClick={() => load(page + 1)}>›</Button>
          </div>
        </div>

        <div className="attendance-evidence__player">
          {selected ? (
            <>
              {videoUrl ? (
                <video key={videoUrl} src={videoUrl} controls autoPlay playsInline />
              ) : (
                <div className="attendance-evidence__placeholder">{videoError || "…"}</div>
              )}
              <div className="attendance-evidence__meta">
                <div><b>{formatDateTime(selected.startedAt)}</b> · {selected.deviceId} · {selected.clipId}</div>
                <ul>
                  {(selected.tags || []).map((tag, idx) => (
                    <li key={idx}>
                      {tag.at ? formatDateTime(tag.at) : ""} <b>{tag.type}</b>
                      {tag.empName ? ` · ${tag.empName}` : ""}
                      {tag.faceId ? ` · face ${tag.faceId}` : ""}
                      {tag.logId ? ` · log ${tag.logId}` : ""}
                      {tag.text ? ` · "${tag.text}"` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="attendance-evidence__placeholder">{t("evidence.pickClip")}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceEvidence;
